import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Divider,
  Chip,
  Link,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

import FaxNumberDisplay from '../../waterAuthorityConfig/components/FaxNumberDisplay';
import MailingAddressDisplay from '../../waterAuthorityConfig/components/MailingAddressDisplay';
import OnlinePortalDisplay from '../../waterAuthorityConfig/components/OnlinePortalDisplay';
import WaterAuthorityUpdatingFields from '../../waterAuthorityConfig/components/WaterAuthorityUpdatingFields';
import WaterAuthoritySubmissionPdfsSection from '../components/WaterAuthoritySubmissionPdfsSection';
import WaterAuthorityMarkSubmittedButton from '../components/WaterAuthorityMarkSubmittedButton';
import ApprovalAutomationsSection from '../components/ApprovalAutomationsSection';

import { BackflowJobGroup } from '../../model';

import PageHeader from '../../../sharedModules/layout/PageHeader';
import PlumbingIcon from '@mui/icons-material/Plumbing';
import axios from '../../../sharedModules/utils/axios.utils';
import { AuthenticationContext } from '../../../homeModules/auth/contexts/AuthenticationContext';

const WaterAuthoritySubmissionReviewPage: React.FC = () => {
  const { backflowJobGroupId } = useParams<{ backflowJobGroupId: string }>();
  const { user } = useContext(AuthenticationContext);

  const isBackflowAdmin = user?.modules?.some(
    (module) => module.value === 'backflow_testing' && parseInt(module.tier) > 2,
  );

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [backflowJobGroup, setBackflowJobGroup] = useState<BackflowJobGroup | null>(null);
  const [generatingPdfs, setGeneratingPdfs] = useState<boolean>(false);
  const [backflowJobItems, setBackflowJobItems] = useState<any[]>([]);

  const fetchBackflowJobGroup = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get<any>(`/backflows/backflow_job_groups/${backflowJobGroupId}`);
      const data = response.data.result;
      setBackflowJobGroup(data);
      await fetchBackflowJobItems();
    } catch (err: any) {
      setError('Failed to load backflow job group details');
    } finally {
      setLoading(false);
    }
  };

  const fetchBackflowJobItems = async () => {
    try {
      const response = await axios.get<any>('/backflows/backflow_job_items', {
        params: { 'ransack[backflow_job_group_id_eq]': backflowJobGroupId },
      });
      const items = response.data.result ?? [];
      setBackflowJobItems(items);
    } catch (err: any) {
      console.error('Failed to fetch backflow job items:', err);
    }
  };

  const generatePdfs = async () => {
    setGeneratingPdfs(true);
    setError('');
    setSuccess('');
    try {
      await axios.post(`/backflows/water_authority_submissions`, { backflow_job_group_id: backflowJobGroupId });
      setSuccess('PDFs generated successfully!');
      await fetchBackflowJobGroup();
    } catch (err: any) {
      setError('Failed to generate some PDFs');
    } finally {
      setGeneratingPdfs(false);
    }
  };

  useEffect(() => {
    fetchBackflowJobGroup();
  }, [backflowJobGroupId]);

  const uniqueTechnicians = React.useMemo(() => {
    const technicianIds = backflowJobItems.map((item) => item.display_emp_id).filter((id) => id && id !== 'Unassigned');
    return [...new Set(technicianIds)];
  }, [backflowJobItems]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!backflowJobGroup) {
    return (
      <Box p={3}>
        <Alert severity="error">Backflow job group not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh', p: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <PageHeader
            titleIcon={<PlumbingIcon />}
            mainTitle={`Review Submission - Order #${backflowJobGroup.order_no}`}
            backButtonLabel="WA Submissions List"
            backButtonLink="/backflow_tests/water_authority_submissions"
          />
        </Grid>

        {error && (
          <Grid item xs={12}>
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          </Grid>
        )}

        {success && (
          <Grid item xs={12}>
            <Alert severity="success" onClose={() => setSuccess('')}>
              {success}
            </Alert>
          </Grid>
        )}

        {/* Job Details Card */}
        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardContent>
              <Grid container spacing={3}>
                {/* Left side - Job Details */}
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <ReceiptIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" fontWeight="600">
                      Job Details
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  <Box sx={{ mb: 2 }}>
                    <Box display="flex" alignItems="flex-start" mb={2}>
                      <Typography variant="body2" fontWeight="500" sx={{ minWidth: 80 }}>
                        Order #:
                      </Typography>
                      <Link
                        component={RouterLink}
                        to={`/backflow_tests/backflow_job_groups/${backflowJobGroup.id}`}
                        variant="body2"
                      >
                        {backflowJobGroup.order_no}
                      </Link>
                    </Box>

                    <Box display="flex" alignItems="flex-start" mb={2}>
                      <BusinessIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                          Customer
                        </Typography>
                        <Typography variant="body1" fontWeight="500">
                          {backflowJobGroup.company_name}
                        </Typography>
                      </Box>
                    </Box>

                    <Box display="flex" alignItems="flex-start" mb={2}>
                      <LocationOnIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                          Address
                        </Typography>
                        <Typography variant="body1">{backflowJobGroup.address1}</Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="center" mt={4} mb={1}>
                    <PersonIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" fontWeight="600">
                      Approval Details
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ mb: 2 }}>
                    <Box display="flex" alignItems="flex-start" mb={2}>
                      <AccessTimeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                          Approved At
                        </Typography>
                        <Typography variant="body1">{backflowJobGroup?.approved_at_display || 'N/A'}</Typography>
                      </Box>
                    </Box>
                    <Box display="flex" alignItems="flex-start" mb={2}>
                      <AccessTimeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                          Submitted At
                        </Typography>
                        <Typography variant="body1">{backflowJobGroup?.submitted_at_display || 'N/A'}</Typography>
                      </Box>
                    </Box>

                    <Box display="flex" alignItems="flex-start" mb={2}>
                      <PersonIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                          Technician{uniqueTechnicians.length !== 1 ? 's' : ''}
                        </Typography>
                        {uniqueTechnicians.length > 0 ? (
                          <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                            {uniqueTechnicians.map((tech, index) => (
                              <Chip key={index} label={tech} size="small" variant="outlined" />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body1" color="text.secondary">
                            No technicians assigned
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} display="flex" flexDirection="column" gap={2}>
          <WaterAuthorityUpdatingFields
            backflowJobGroup={backflowJobGroup}
            fetchBackflowJobGroup={fetchBackflowJobGroup}
          />
          <MailingAddressDisplay backflowJobGroup={backflowJobGroup} />
          <FaxNumberDisplay backflowJobGroup={backflowJobGroup} />
          <OnlinePortalDisplay backflowJobGroup={backflowJobGroup} />
        </Grid>

        {/* Right column: Authority fields + Submission Actions */}
        <Grid item xs={6}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="600">
                Submission Actions
              </Typography>

              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={1}>
                <Grid item xs={12} style={{ marginBottom: 6 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={generatingPdfs ? <CircularProgress size={20} /> : <PictureAsPdfIcon />}
                    onClick={generatePdfs}
                    disabled={generatingPdfs || !backflowJobGroup.authority_has_pdf_attached}
                    fullWidth
                    size="large"
                    sx={{ py: 1.5 }}
                  >
                    {generatingPdfs ? 'Generating...' : 'Regenerate Reports'}
                  </Button>
                  {!backflowJobGroup.authority_has_pdf_attached && (
                    <Alert severity="warning">No PDF template for this water authority.</Alert>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <WaterAuthorityMarkSubmittedButton backflowJobGroup={backflowJobGroup} />
                </Grid>
              </Grid>

              {!['approved', 'submitted'].includes(backflowJobGroup.status) && (
                <Box mt={3}>
                  <Alert severity="info">
                    Job group must be in 'approved' status to submit. Current status:{' '}
                    {backflowJobGroup.status || 'unknown'}
                  </Alert>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          {isBackflowAdmin && <ApprovalAutomationsSection backflowJobGroupId={backflowJobGroupId ?? ''} />}
        </Grid>

        {/* PDF List Section Header + fetch approved items */}
        <Grid item xs={12}>
          <WaterAuthoritySubmissionPdfsSection backflowJobGroupId={backflowJobGroupId ?? ''} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default WaterAuthoritySubmissionReviewPage;
